import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { PageHeader } from '../../components/ui/PageHeader';
import { apiClient } from '../../lib/api-client';

interface AuditRow {
  id: string;
  action: string;
  actorUserId: string | null;
  actorUser: { id: string; fullName: string; email: string } | null;
  targetType: string | null;
  targetId: string | null;
  metadata: unknown;
  createdAt: string;
}

interface ListResponse {
  data: AuditRow[];
  meta: { page: number; pageSize: number; total: number };
}

// Libellés lisibles des actions (repli sur le code brut si inconnu).
const ACTION_LABEL: Record<string, string> = {
  'auth.login': 'Connexion',
  'auth.login_2fa': 'Connexion (2FA)',
  'auth.logout': 'Déconnexion',
  'auth.password_reset': 'Réinitialisation du mot de passe',
  'auth.2fa_enabled': 'Activation 2FA',
  'auth.2fa_disabled': 'Désactivation 2FA',
  'organization.created': 'Organisation créée',
  'organization.updated': 'Organisation modifiée',
  'organization.suspend_toggled': 'Organisation suspendue/réactivée',
  'organization.settings_updated': "Paramètres d'organisation modifiés",
  'automation.created': 'Automatisation créée',
  'automation.updated': 'Automatisation modifiée',
  'automation.deleted': 'Automatisation supprimée',
  'automation.retired': 'Automatisation retirée',
  'automation.triggered': 'Automatisation déclenchée',
  'execution.retried': 'Exécution relancée',
  'execution.deleted': 'Exécution supprimée',
  'incident.updated': 'Incident modifié',
  'automation_request.created': 'Demande d’automatisation envoyée',
  'automation_request.updated': 'Demande d’automatisation mise à jour',
  'incident.deleted': 'Incident supprimé',
  'user.invited': 'Utilisateur invité',
  'user.invite_accepted': 'Invitation acceptée',
  'user.updated': 'Utilisateur modifié',
  'user.deactivated': 'Utilisateur désactivé',
  'user.deleted': 'Utilisateur supprimé',
  'user.automation_access_updated': 'Accès aux automatisations modifié',
  'report.generate_requested': 'Rapport demandé',
  'notification_channel.created': 'Canal de notification créé',
  'notification_channel.updated': 'Canal de notification modifié',
  'notification_channel.deleted': 'Canal de notification supprimé',
  'notification_rule.created': 'Règle de notification créée',
  'notification_rule.updated': 'Règle de notification modifiée',
  'notification_rule.deleted': 'Règle de notification supprimée',
  'notification_preferences.updated': 'Préférences de notification modifiées',
};

const TARGET_LABEL: Record<string, string> = {
  Organization: 'Organisation',
  Automation: 'Automatisation',
  Execution: 'Exécution',
  Incident: 'Incident',
  User: 'Utilisateur',
  Report: 'Rapport',
  NotificationChannel: 'Canal de notification',
  NotificationRule: 'Règle de notification',
  IngestionEvent: 'Événement entrant',
};

async function fetchAuditLogs(action: string): Promise<ListResponse> {
  const res = await apiClient.get<ListResponse>('/audit-logs', {
    params: { page: 1, pageSize: 100, action: action || undefined },
  });
  return res.data;
}

export function AuditLogPage() {
  const [action, setAction] = useState('');
  const query = useQuery({
    queryKey: ['audit-logs', action],
    queryFn: () => fetchAuditLogs(action),
    refetchInterval: 15_000,
  });

  return (
    <>
      <PageHeader
        helpTopic="audit"
        title="Journal d'audit"
        subtitle="Qui a fait quoi, et quand — actions sensibles tracées."
      />
      <section className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4 flex items-center gap-2">
          <input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="Filtrer par action (ex. automation.deleted)"
            className="w-72 rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
          <span className="text-xs text-slate-400">{query.data?.meta.total ?? 0} entrée(s)</span>
        </div>

        <div className="overflow-x-auto surface">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Date
                </th>
                <th scope="col" className="px-4 py-3">
                  Action
                </th>
                <th scope="col" className="px-4 py-3">
                  Cible
                </th>
                <th scope="col" className="px-4 py-3">
                  Auteur
                </th>
                <th scope="col" className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(query.data?.data ?? []).map((row) => (
                <tr key={row.id} className="align-top hover:bg-slate-50">
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">
                    {new Date(row.createdAt).toLocaleString('fr-FR')}
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">
                    {ACTION_LABEL[row.action] ?? row.action}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {row.targetType ? (TARGET_LABEL[row.targetType] ?? row.targetType) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {row.actorUser ? row.actorUser.fullName : 'Système'}
                  </td>
                  <td className="px-4 py-2.5">
                    <details className="text-xs">
                      <summary className="cursor-pointer text-brand">détails</summary>
                      <div className="mt-1 space-y-1 text-[11px] text-slate-500">
                        {row.actorUser && <div>Auteur : {row.actorUser.email}</div>}
                        {row.targetId && <div>Réf. cible : {row.targetId}</div>}
                        <div>Code : {row.action}</div>
                        {row.metadata != null && Object.keys(row.metadata as object).length > 0 && (
                          <pre className="mt-1 max-w-md overflow-x-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-slate-600">
                            {JSON.stringify(row.metadata, null, 2)}
                          </pre>
                        )}
                      </div>
                    </details>
                  </td>
                </tr>
              ))}
              {query.data && query.data.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                    Aucune entrée d'audit.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
